import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useCreateReviewMutation } from "../../store/api/reviewsApi";
import { getApiErrorMessage } from "../../store/api/baseApi";
import { decryptReviewLink } from "../../utils/reviewLinkCrypto";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import "./LeaveReviewPage.css";

const StarSelector: React.FC<{ value: number; onChange: (v: number) => void }> = ({
  value,
  onChange,
}) => (
  <div className="lr-stars">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        className={`lr-star-btn${value >= n ? " lr-star-btn--active" : ""}`}
        onClick={() => onChange(n)}
        aria-label={`Rate ${n} star${n > 1 ? "s" : ""}`}
      >
        <i className={`bi bi-star${value >= n ? "-fill" : ""}`} />
      </button>
    ))}
    {value > 0 && <span className="lr-star-label">{value} / 5</span>}
  </div>
);

type LocationState = "idle" | "detecting" | "done" | "denied" | "error";

const LeaveReviewPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("d");

  const [prefillName, setPrefillName] = useState("");
  const [prefillProject, setPrefillProject] = useState("");
  const [decrypting, setDecrypting] = useState(Boolean(token));

  const [name, setName] = useState("");
  const [city, setCity] = useState("");
  const [cityLocked, setCityLocked] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [locationState, setLocationState] = useState<LocationState>("idle");

  const cityRef = useRef<HTMLInputElement>(null);

  const isExistingCustomer = Boolean(prefillName);

  const [createReview, { isLoading, error }] = useCreateReviewMutation();
  const apiError = error ? getApiErrorMessage(error as FetchBaseQueryError) : "";

  useEffect(() => {
    if (!token) { setDecrypting(false); return; }
    decryptReviewLink(token).then((data) => {
      if (data) {
        setPrefillName(data.name ?? "");
        setPrefillProject(data.project ?? "");
        setName(data.name ?? "");
        if (data.city) {
          setCity(data.city);
          setCityLocked(true);
        }
      }
      setDecrypting(false);
    });
  }, [token]);

  const handleDetectLocation = () => {
    if (!navigator.geolocation) { setLocationState("error"); return; }
    setLocationState("detecting");
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`,
            { headers: { "Accept-Language": "en" } }
          );
          const json = await res.json();
          const addr = json.address ?? {};
          const detected =
            addr.city || addr.town || addr.village || addr.county || addr.state_district || "";
          setCity(detected);
          setLocationState("done");
          cityRef.current?.focus();
        } catch {
          setLocationState("error");
        }
      },
      () => setLocationState("denied"),
      { timeout: 8000 }
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError("");

    const trimName = name.trim();
    const trimCity = city.trim();
    const trimComment = comment.trim();

    if (!trimName) { setValidationError("Please enter your name."); return; }
    if (!trimCity) { setValidationError("Please enter your city."); return; }
    if (rating < 1) { setValidationError("Please select a star rating."); return; }
    if (!trimComment) { setValidationError("Please write a short comment."); return; }

    try {
      await createReview({
        customerName: trimName,
        city: trimCity,
        projectTitle: prefillProject,
        rating,
        comment: trimComment,
        source: "website",
        status: "pending",
        projectId: null,
      }).unwrap();
      setSubmitted(true);
    } catch {
      /* error shown via apiError */
    }
  };

  if (decrypting) {
    return (
      <div className="lr-page">
        <div className="lr-card lr-card--center">
          <div className="lr-spinner" />
          <p className="lr-loading-txt">Loading your review form…</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="lr-page">
        <div className="lr-card lr-card--success">
          <div className="lr-success-icon">
            <i className="bi bi-check-circle-fill" />
          </div>
          <h2 className="lr-success-title">Thank you, {name.split(" ")[0]}!</h2>
          <p className="lr-success-msg">
            Your review has been submitted and will appear on our website after approval.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="lr-page">
      <div className="lr-card">
        <div className="lr-brand">
          <img src="/images/dialin24-logo.png" alt="Dialin24 Construction" className="lr-logo" />
        </div>

        <h1 className="lr-title">Share Your Experience</h1>
        <p className="lr-subtitle">
          {isExistingCustomer
            ? `Hi ${prefillName.split(" ")[0]}, how would you rate your experience with us?`
            : "We'd love to hear from you. Your review helps other customers trust us."}
        </p>

        {prefillProject && (
          <div className="lr-project-badge">
            <i className="bi bi-building" /> {prefillProject}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          {!isExistingCustomer && (
            <div className="lr-field">
              <label className="lr-label" htmlFor="lr-name">
                Your Name
              </label>
              <input
                id="lr-name"
                type="text"
                className="form-control lr-input"
                placeholder="Full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={100}
              />
            </div>
          )}

          {/* City — hidden when pre-filled by admin, same as name */}
          {!cityLocked && (
            <div className="lr-field">
              <label className="lr-label" htmlFor="lr-city">
                City
              </label>
              <div className="lr-city-row">
                <input
                  id="lr-city"
                  ref={cityRef}
                  type="text"
                  className="form-control lr-input"
                  placeholder="Your city"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  maxLength={100}
                />
                <button
                  type="button"
                  className={`lr-location-btn${locationState === "detecting" ? " lr-location-btn--busy" : ""}${locationState === "done" ? " lr-location-btn--done" : ""}`}
                  onClick={handleDetectLocation}
                  disabled={locationState === "detecting"}
                  title="Detect my location"
                  aria-label="Detect my location"
                >
                  {locationState === "detecting" ? (
                    <span className="lr-btn-spinner" />
                  ) : locationState === "done" ? (
                    <i className="bi bi-geo-alt-fill" />
                  ) : (
                    <i className="bi bi-geo-alt" />
                  )}
                </button>
              </div>
              {locationState === "denied" && (
                <p className="lr-loc-hint lr-loc-hint--warn">
                  Location access was denied. Please type your city manually.
                </p>
              )}
              {locationState === "error" && (
                <p className="lr-loc-hint lr-loc-hint--warn">
                  Could not detect location. Please enter your city manually.
                </p>
              )}
              {locationState === "done" && city && (
                <p className="lr-loc-hint lr-loc-hint--ok">
                  <i className="bi bi-check2" /> Location detected: {city}
                </p>
              )}
            </div>
          )}

          <div className="lr-field">
            <label className="lr-label">Rating</label>
            <StarSelector value={rating} onChange={setRating} />
          </div>

          <div className="lr-field">
            <label className="lr-label" htmlFor="lr-comment">
              Your Comments
            </label>
            <textarea
              id="lr-comment"
              className="form-control lr-input"
              placeholder="Tell us about your experience…"
              rows={4}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={1000}
            />
            <p className="lr-char-count">{comment.length} / 1000</p>
          </div>

          {(validationError || apiError) && (
            <div className="alert alert-danger py-2 mb-3">
              <i className="bi bi-exclamation-triangle-fill me-2" />
              {validationError || apiError}
            </div>
          )}

          <button type="submit" className="lr-submit-btn" disabled={isLoading}>
            {isLoading ? "Submitting…" : "Submit Review"}
          </button>
        </form>

        <p className="lr-disclaimer">
          Your review will be visible on our website after review by our team.
        </p>
      </div>
    </div>
  );
};

export default LeaveReviewPage;
